import React, { useState, useRef, useEffect } from 'react';
import { Outlet, Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLayout } from '../context/LayoutContext';
import { useShoppingList } from '../context/ShoppingListContext';
import ShoppingList from './ShoppingList';
import Breadcrumb from './Breadcrumb';
import { FOOTER } from '../constants/strings';
import {
  IconNavGrid, IconNavCalendar, IconNavFridge, IconNavBook, IconNavSearch,
  IconNavShopping, IconNavSettings, IconNavLogout, IconNavAdmin,
  IconNavCamera, IconNavFile, IconNavImport, IconNavPlus,
  IconBell, IconMail, IconChevUp, IconChevDown,
  IconRefreshAction, IconLeaf, IconIdea,
} from './icons';
import './Layout.css';

function sbClass({ isActive }) {
  return 'sb-item' + (isActive ? ' active' : '');
}

/* ── Linke Sidebar ── */
function Sidebar({ mobileOpen, onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { uncheckedCount } = useShoppingList();
  const initial = (user?.username || '?')[0].toUpperCase();
  const [shoppingOpen, setShoppingOpen] = useState(false);

  async function handleLogout() {
    await logout();
    navigate('/dashboard');
  }

  return (
    <>
      {/* Mobile Backdrop */}
      {mobileOpen && <div className="sb-backdrop" onClick={onClose} />}

      <aside className={'sidebar' + (mobileOpen ? ' sidebar-open' : '')}>

        {/* Hauptnavigation */}
        <div className="sb-group">
          {user && (
            <NavLink to="/dashboard" className={sbClass} onClick={onClose}>
              <span className="sb-ico"><IconNavGrid /></span>
              Startseite
            </NavLink>
          )}
          {user && (
            <NavLink to="/woche" className={sbClass} onClick={onClose}>
              <span className="sb-ico"><IconNavCalendar /></span>
              Wochenplaner
              <span className="sb-badge sb-badge-soon">Bald</span>
            </NavLink>
          )}
          <NavLink to={user ? "/kuehlschrank/inventar" : "/dashboard"} className={sbClass} onClick={onClose}>
            <span className="sb-ico"><IconNavFridge /></span>
            Mein Inventar
          </NavLink>
          {user && (
            <NavLink to="/my-recipes" className={sbClass} onClick={onClose}>
              <span className="sb-ico"><IconNavBook /></span>
              Meine Rezepte
            </NavLink>
          )}
          <NavLink to="/rezepte" className={sbClass} onClick={onClose}>
            <span className="sb-ico"><IconNavSearch /></span>
            Alle Rezepte
          </NavLink>
          {user && (
            <button
              className={'sb-item' + (shoppingOpen ? ' active' : '')}
              onClick={() => setShoppingOpen(o => !o)}
            >
              <span className="sb-ico"><IconNavShopping /></span>
              <span className="sb-item-label">Einkaufsliste</span>
              {uncheckedCount > 0 && (
                <span className="sb-badge sb-badge-new">{uncheckedCount}</span>
              )}
              <span className="sb-shopping-chevron">{shoppingOpen ? <IconChevUp size={14} /> : <IconChevDown size={14} />}</span>
            </button>
          )}
          {user && shoppingOpen && (
            <div className="sb-shopping-panel">
              <ShoppingList hideHeader={true} />
            </div>
          )}
        </div>

        <div className="sb-divider" />

        {/* Rezept hinzufügen */}
        <div className="sb-group">
          <div className="sb-label">Rezept hinzufügen</div>
          <Link to="/kuehlschrank" className="sb-item sb-item-highlight" onClick={onClose}>
            <span className="sb-ico"><IconNavCamera /></span>
            Kühlschrank scannen
          </Link>
          <Link to="/upload" className="sb-item" onClick={onClose}>
            <span className="sb-ico"><IconNavFile /></span>
            Rezept scannen
          </Link>
          <Link to="/importieren" className="sb-item" onClick={onClose}>
            <span className="sb-ico"><IconNavImport /></span>
            Externer Rezept-Import
          </Link>
          {user && (
            <Link to="/new" className="sb-item" onClick={onClose}>
              <span className="sb-ico"><IconNavPlus /></span>
              Manuelle Eingabe
            </Link>
          )}
        </div>

        <div className="sb-divider" />

        {/* Kochbücher – Platzhalter */}
        {user && (
          <div className="sb-group">
            <div className="sb-label">
              Kochbücher
              <span className="sb-label-wip">In Arbeit</span>
            </div>
            <div className="sb-item sb-item-wip">
              <span className="sb-dot" style={{ background: 'var(--green)' }} />
              Eigene Kochbücher
            </div>
            <div className="sb-item sb-item-wip">
              <span className="sb-dot" style={{ background: 'var(--blue)' }} />
              Geteilte Sammlungen
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="sb-footer">
          <div className="sb-divider" />
          {user ? (
            <>
              <NavLink to="/einstellungen" className={sbClass} onClick={onClose}>
                <span className="sb-ico"><IconNavSettings /></span>
                Einstellungen
              </NavLink>
              {user.is_admin && (
                <NavLink to="/admin" className={sbClass} onClick={onClose}>
                  <span className="sb-ico"><IconNavAdmin /></span>
                  Administration
                </NavLink>
              )}
              <button className="sb-item sb-item-logout" onClick={handleLogout}>
                <span className="sb-ico"><IconNavLogout /></span>
                Abmelden
              </button>
              <div className="sb-user-card">
                <div className="sb-user-av">{initial}</div>
                <div className="sb-user-info">
                  <div className="sb-user-name">{user.username}</div>
                  <div className="sb-user-plan">
                    {user.is_admin ? 'Administrator' : 'Mitglied'}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <Link to="/login?tab=register" className="sb-item sb-item-highlight" onClick={onClose}>
                <span className="sb-ico"><IconNavPlus /></span>
                Kostenlos registrieren
              </Link>
              <Link to="/login" className="sb-item" onClick={onClose}>
                <span className="sb-ico"><IconNavSettings /></span>
                Anmelden
              </Link>
            </>
          )}
        </div>

      </aside>
    </>
  );
}

// Demo-Nachrichten — später durch echte Daten aus Wochenplaner + Kühlschrank ersetzt
const DEMO_MESSAGES = [
  {
    icon: <IconRefreshAction size={16} />,
    text: 'Vor 3 Wochen hast du Wiener Schnitzel gemacht — soll ich es diese Woche wieder einplanen?',
    action: { label: 'Zum Rezept', to: '/rezepte' },
  },
  {
    icon: <IconNavFridge size={16} />,
    text: 'Im Kühlschrank-Scan von letzter Woche war noch Hackfleisch — passt gut zu Faschierte Laibchen.',
    action: { label: 'Rezept ansehen', to: '/rezepte' },
  },
  {
    icon: <IconIdea size={16} />,
    text: 'Tipp: Verbinde den Wochenplaner mit dem Kühlschrank-Scan für persönliche Vorschläge hier.',
    action: null,
  },
];

/* ── Mobile Bottom-Navigation ── */
function BottomNav() {
  return (
    <nav className="bottom-nav" aria-label="Mobile Navigation">
      <NavLink to="/dashboard" className={({ isActive }) => 'bn-item' + (isActive ? ' bn-item--active' : '')}>
        <IconNavGrid className="bn-icon" size={22} />
        <span className="bn-label">Start</span>
      </NavLink>

      <NavLink to="/rezepte" className={({ isActive }) => 'bn-item' + (isActive ? ' bn-item--active' : '')}>
        <IconNavSearch className="bn-icon" size={22} />
        <span className="bn-label">Rezepte</span>
      </NavLink>

      <NavLink to="/kuehlschrank" className={({ isActive }) => 'bn-item bn-item--scan' + (isActive ? ' bn-item--active' : '')}>
        <span className="bn-scan-btn">
          <IconNavCamera className="bn-icon bn-icon--scan" size={22} />
        </span>
        <span className="bn-label">Scan</span>
      </NavLink>

      <NavLink to="/einstellungen" className={({ isActive }) => 'bn-item' + (isActive ? ' bn-item--active' : '')}>
        <IconNavSettings className="bn-icon" size={22} />
        <span className="bn-label">Profil</span>
      </NavLink>
    </nav>
  );
}

/* ── Haupt-Layout ── */
function Layout() {
  const { globalCfg } = useLayout();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const notifyRef = useRef(null);
  const initial = (user?.username || '?')[0].toUpperCase();

  useEffect(() => {
    function handleClickOutside(e) {
      if (notifyRef.current && !notifyRef.current.contains(e.target)) {
        setNotifyOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="layout">

      {/* ── Skip Navigation ── */}
      <a href="#main-content" className="skip-nav">Zum Inhalt springen</a>

      {/* ── Top Nav ── */}
      <nav className="topnav">
        <div className="tn-logo">
          {/* Hamburger auf Mobile – immer sichtbar */}
          <button className="tn-hamburger" onClick={() => setSidebarOpen(o => !o)} aria-label="Menü">
            <span /><span /><span />
          </button>
          <Link to={user ? '/dashboard' : '/rezepte'} className="logo-link">
            <div className="logo-mark">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="5.5" cy="6.5" r="1.5" fill="currentColor"/>
                <circle cx="10.5" cy="6.5" r="1.5" fill="currentColor"/>
                <path d="M8 4C5.5 2 2 3 2 6.5C2 10 5 13 8 13C11 13 14 10 14 6.5C14 3 10.5 2 8 4Z"
                  stroke="currentColor" strokeWidth="1.2" fill="none"/>
                <path d="M6 10C6.5 10.8 7 11 8 11C9 11 9.5 10.8 10 10"
                  stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" fill="none"/>
                <path d="M5 4L3 2M11 4L13 2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
            </div>
            <span className="logo-text">
              strix<em>ly</em>
            </span>
          </Link>
        </div>

        <div className="tn-mid">
          <div className="searchbar" role="search">
            <IconNavSearch size={16} />
            <input
              type="search"
              className="searchbar-input"
              placeholder="Rezepte, Zutaten, Kategorien…"
              aria-label="Rezepte suchen"
              onKeyDown={e => { if (e.key === 'Enter') navigate('/rezepte'); }}
            />
          </div>
        </div>

        <div className="tn-right">
          {process.env.REACT_APP_ENV === 'development' && (
            <span className="dev-env-badge">Dev</span>
          )}
          {user ? (
            <>
              {/* Nachrichten-Platzhalter */}
              <Link to="/nachrichten" className="tn-icon-btn" aria-label="Nachrichten">
                <IconMail size={20} />
              </Link>
              {/* Benachrichtigungen */}
              <div className="di-notify" ref={notifyRef}>
                <button
                  className={`tn-icon-btn tn-icon-btn--bell${notifyOpen ? ' tn-icon-btn--open' : ''}`}
                  onClick={() => setNotifyOpen(o => !o)}
                  aria-label="Benachrichtigungen"
                >
                  <IconBell size={20} />
                  {/* Count-Badge erst anzeigen wenn echte Benachrichtigungen vorhanden */}
                </button>
                {notifyOpen && (
                  <div className="di-notify-overlay">
                    <div className="di-notify-header">Für dich</div>
                    <ul className="di-messages">
                      {DEMO_MESSAGES.map((msg, i) => (
                        <li key={i} className="di-msg">
                          <span className="di-msg-icon">{msg.icon}</span>
                          <div className="di-msg-body">
                            <span className="di-msg-text">{msg.text}</span>
                            {msg.action && (
                              <Link to={msg.action.to} className="di-msg-action" onClick={() => setNotifyOpen(false)}>
                                {msg.action.label} →
                              </Link>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div className="tn-avatar">{initial}</div>
            </>
          ) : (
            <>
              <Link to="/login" className="topbtn tb-ghost">Anmelden</Link>
              <Link to="/login?tab=register" className="topbtn tb-primary">Registrieren</Link>
            </>
          )}
        </div>
      </nav>

      {/* ── Body: Sidebar + Content ── */}
      <div className="layout-body">
        <Sidebar mobileOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="layout-right">
          <Breadcrumb />
          <main className="layout-content" id="main-content">
            <Outlet />
          </main>
        </div>
      </div>

      {/* ── Mobile Bottom-Nav ── */}
      {user && <BottomNav />}

      {/* ── Footer ── */}
      <footer className="footer">
        <div className="footer-inner">
          <p>{globalCfg.footerText || `© ${new Date().getFullYear()} Strixly`}</p>
          <div className="footer-feedback">
            <span className="footer-feedback-label">Beta-Feedback:</span>
            <Link to="/feedback?tab=bug" className="footer-feedback-btn">
              Bug melden
            </Link>
            <Link to="/feedback?tab=idea" className="footer-feedback-btn">
              Idee schicken
            </Link>
          </div>
          <div className="footer-links">
            <Link to="/impressum">Impressum</Link>
            <Link to="/datenschutz">Datenschutz</Link>
            <Link to="/agb">AGB</Link>
          </div>
        </div>
        <div className="footer-disclaimer">
          {FOOTER.disclaimer}
        </div>
      </footer>

    </div>
  );
}

export default Layout;
